import { ReactNode, useEffect, useState } from "react";
import { Button } from "@/components/Base/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/Base/Dialog";
import { Card, IconNames, Shop } from "@/types/v2";
import { ClassNameValue } from "tailwind-merge";
import { cn } from "@/lib/utils";
import { useCustomerUpdatesByCardId } from "@/hooks/queries/useCustomerUpdatesByCardId";
import { Icon } from "@/components/Common/Icon";
import { CustomerUpdatesTimeline } from "./CustomerUpdatesTimeline";

interface CustomerUpdatesTimelineDialogProps {
  title?: string;
  buttonTitle?: string;
  buttonIcon?: ReactNode;
  buttonClasses?: ClassNameValue;
  description?: string;
  trigger?: ReactNode;
  externalTrigger?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  cardData: Card;
  shop: Shop;
}

export const CustomerUpdatesTimelineDialog = ({
  title,
  buttonTitle,
  buttonIcon,
  buttonClasses,
  description,
  trigger,
  externalTrigger,
  open,
  onOpenChange,
  cardData,
  shop,
}: CustomerUpdatesTimelineDialogProps) => {
  const [isOpen, setIsOpen] = useState(open ?? false);

  // Get all customer updates for this card
  const {
    data: customerUpdates = [],
    isLoading: isLoadingCustomerUpdates,
    error: customerUpdatesError,
  } = useCustomerUpdatesByCardId({
    shopId: shop.id,
    cardId: cardData.id,
    enabled: isOpen, // Only fetch when dialog is open
  });

  // Sync local state with the controlled `open` prop
  useEffect(() => {
    if (typeof open === "boolean") {
      setIsOpen(open);
    }
  }, [open]);

  // Handle the dialog's open state changes
  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen);
    onOpenChange?.(newOpen); // Notify parent if `onOpenChange` is provided
  };

  return (
    <Dialog onOpenChange={handleOpenChange} open={isOpen}>
      {trigger && !externalTrigger ? (
        // Use custom trigger if provided
        <DialogTrigger asChild>{trigger}</DialogTrigger>
      ) : (
        // Default trigger button
        !externalTrigger && (
          <DialogTrigger asChild>
            <Button
              className={cn(buttonClasses)}
              variant={"ghost-primary"}
              onClick={() => handleOpenChange(true)}
            >
              {buttonIcon}
              {buttonTitle ?? "Customer Updates Timeline"}
            </Button>
          </DialogTrigger>
        )
      )}

      <DialogContent className="flex max-h-[90vh] max-w-5xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>
            {title ??
              `Customer Updates Timeline - RO ${cardData.cardNumber ?? cardData.id}`}
          </DialogTitle>
        </DialogHeader>
        <DialogDescription>
          {description ??
            "View all customer updates for this job in chronological order."}
        </DialogDescription>

        {isLoadingCustomerUpdates ? (
          <div className="justify-centerpy-8 flex items-center">
            <div className="flex items-center gap-2">
              <Icon name={IconNames.loader} className="h-5 w-5 animate-spin" />
              <span>Loading customer updates...</span>
            </div>
          </div>
        ) : customerUpdatesError ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Icon
              name={IconNames.warning}
              className="mb-2 h-8 w-8 text-destructive"
            />
            <h3 className="mb-1 text-lg font-medium text-destructive">
              Error Loading Customer Updates
            </h3>
            <p className="text-sm text-muted-foreground">
              Unable to load customer updates. Please try again.
            </p>
          </div>
        ) : (
          <div className="overflow-auto">
            <CustomerUpdatesTimeline
              updates={customerUpdates}
              card={cardData}
              compact={false}
              initiallyOpenCount={3}
              showHeader={false}
              emptyState={
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Icon
                    name="bell"
                    className="mb-2 h-8 w-8 text-muted-foreground"
                  />
                  <h3 className="mb-1 text-lg font-medium text-muted-foreground">
                    No Customer Updates
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    There are no customer updates for this card yet.
                  </p>
                </div>
              }
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
